

import Mountains from "@/assets/img/layout/profile/header-mountains.webp";
import CameraIcon from "@/assets/img/symbolic/camera.svg?react";
import EditorHatIcon from "@/assets/img/symbolic/editor-hat.svg?react";
// import ChevronLeftIcon from "@/assets/img/symbolic/chevron-left.svg?react";
import { ProfileTab } from "@/pages/home/Profile/ProfileTypes";
import type { ProfileDataStats } from "@/local-shared/returnTypes";
import social from "@/api/social";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "@/Realize";
import DEFAULT_AVATAR from "@/assets/img/r-icon.svg?react";
import VerifiedBadge from "@/assets/img/symbolic/verified-badge.svg?react";
import UIListener from "@/pages/home/UIListener";
import SettingsIcon from "@/assets/img/symbolic/settings-cog.svg?react";
import type { TabName } from "@/pages/home/TabData";
import TextPlaceholder from "../TextPlaceholder";
import TwitchIcon from "@/assets/img/icons/twitch.svg?react";
import type { DBAccount } from "@/local-shared/model";
import { isTwitchConnected } from "@/util/Socials";

export function TabButton({
    name,
    isActive,
    icon,
    onClick,
}: {
    name: string,
    isActive: boolean,
    icon?: React.ReactNode,
    onClick: () => void
})
{
    return (
        <button
            className={Wind.style({
                display: "flex",
                gap: "gap-2",
                paddingY: "py-1",
                fontWeight: isActive ? "font-bold" : "font-normal",
                borderBottomWidth: "border-b",
                color: isActive ? "text-white" : "text-white/60",
                paddingX: "px-2",
                borderBottomColor: isActive ? "border-b-poppy-yellow" : "border-b-transparent"
            }).class}
            onClick={onClick}
        >
            {icon}
            {name}
        </button>
    );
}

export function ProfileStat({
    name,
    value
}: {
    name: string,
    value: number
})
{
    return (
        <div
            className={Wind.style({
                display: "flex",
                gap: "gap-2",
                color: "text-[#E6D6C4]",
                fontSize: "text-xs",
                lineHeight: "leading-[0.75rem]",
                marginBottom: "mb-2",
            }).class}
        >
            <span
                className={Wind.style({
                    fontWeight: "font-semibold",
                    color: "text-white",
                }).class}
            >
                {value}
            </span>
            <span>
                {value !== 1 ? `${name}s` : name}
            </span>
        </div>
    );
}

function FollowButton({
    profile_id,
    isFollowing = false,
    onChange,
}: {
    profile_id: number;
    isFollowing?: boolean;
    onChange?: () => void;
})
{
    const [isLoading, setIsLoading] = useState(false);

    return (
        <button
            className={Wind.style({
                display: "flex",
                alignItems: "items-center",
                justifyContent: "justify-center",
                paddingX: "px-3",
                borderRadius: "rounded-full",
                backgroundColor: isFollowing ? "bg-sharp-red" : "bg-[#F9DC5C]",
                color: isFollowing ? "text-white" : "text-dark-blue",
                fontFamily: "font-poppins",
                fontSize: "text-xs",
                fontWeight: isFollowing ? "font-bold" : "font-normal",
                lineHeight: "leading-6",
                width: "w-32",
                ":disabled": {
                    opacity: "disabled:opacity-50",
                    cursor: "disabled:cursor-not-allowed",
                    backgroundColor: isFollowing ? "disabled:bg-sharp-red/50" : "disabled:bg-[#F9DC5C]/50",
                }
            }).class}
            disabled={isLoading}
            onClick={() =>
            {
                setIsLoading(true);

                // Handle follow action
                social.followProfile(profile_id).then((result) =>
                {
                    if (typeof result === "boolean")
                    {
                        Logger.warn("Follow action returned no result");

                        return;
                    }

                    Logger.log(result.message);
                    onChange?.();
                })
                    .catch((error) =>
                    {
                        Logger.error("Failed to follow profile:", error);
                    })
                    .finally(() =>
                    {
                        setIsLoading(false);
                    });
            }}
        >
            {isFollowing ? "Unfollow" : "Follow"}
        </button>
    );
}

function ProfileTopControls({
    profileData,
    setActiveTab,
    setContextMenuVisible,
    onBackPress,
}: {
    profileData: DBAccount|null;
    setActiveTab: (tab: TabName) => void;
    setContextMenuVisible: (visible: boolean) => void;
    onBackPress: () => void;
})
{
    const userData = useContext(AuthContext);

    if (!userData || !profileData)
    {
        return (
            <div
                className={Wind.style({
                    height: "h-10",
                }).class}
            />
        );
    }

    if (userData.id.toString() === profileData.id.toString())
    {
        return (
            <div
                className={Wind.style({
                    width: "w-full",
                    display: "flex",
                    alignItems: "items-center",
                    justifyContent: "justify-end",
                    gap: "gap-2",
                    paddingX: "px-4",
                    paddingY: "py-2",
                }).class}
            >
                <button
                    className={Wind.style({
                        display: "flex",
                        height: "h-8",
                        alignItems: "items-center",
                        justifyContent: "justify-center",
                        paddingX: "px-3",
                        paddingY: "py-2",
                        gap: "gap-1",
                        borderRadius: "rounded-full",
                        borderWidth: "border",
                        borderColor:  "border-[#524435]",

                        backgroundColor: "bg-transparent",

                        fontFamily: "font-poppins",
                        fontSize: "text-xs",
                        lineHeight: "leading-3",
                        color: "text-[#524435]",
                    }).class}
                    onClick={() =>
                    {
                        UIListener.trigger("openProfileEditMode", {});
                    }}
                >
                    Edit Profile
                </button>
                <button
                    className={Wind.style({
                        display: "flex",
                        height: "h-8",
                        alignItems: "items-center",
                        justifyContent: "justify-center",
                        aspectRatio: "aspect-square",
                        borderRadius: "rounded-full",
                        borderWidth: "border",
                        borderColor: "border-[#524435]",

                        fontFamily: "font-poppins",
                        fontSize: "text-xs",
                        lineHeight: "leading-3",
                        color: "text-[#524435]",
                    }).class}
                    onClick={() =>
                    {
                        Logger.log("Goto Settings");
                        setActiveTab("settings");
                    }}
                >
                    <SettingsIcon />
                </button>
            </div>
        );
    }

    return (
        <div
            className={Wind.style({
                width: "w-full",
                display: "flex",
                alignItems: "items-center",
                justifyContent: "justify-between",
                gap: "gap-2",
                paddingX: "px-4",
                paddingY: "py-2",
            }).class}
        >
            <button
                className={Wind.style({
                    width: "w-8",
                    height: "h-8",
                    display: "flex",
                    alignItems: "items-center",
                    justifyContent: "justify-center",
                    borderRadius: "rounded-full",
                    borderColor: "border-[#524435]",
                    borderWidth: "border",
                }).class}
                id="profile-back-button"
                onClick={onBackPress}
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        // eslint-disable-next-line @stylistic/max-len
                        d="M13.7467 4.37479L8.12172 9.99979L13.7467 15.6248L11.9789 17.3926L4.58618 9.99979L11.9789 2.60703L13.7467 4.37479Z"
                        fill="#524435"
                    />
                </svg>
            </button>
            <button
                className={Wind.style({
                    borderColor: "border-[#524435]",
                    borderWidth: "border",
                    width: "w-8",
                    height: "h-8",
                    display: "flex",
                    alignItems: "items-center",
                    justifyContent: "justify-center",
                    borderRadius: "rounded-full",
                    backgroundColor: "bg-transparent",
                }).class}
                onClick={() =>
                {
                    setContextMenuVisible(true);
                }}
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        // eslint-disable-next-line @stylistic/max-len
                        d="M3.33333 9.99999C3.33333 10.221 3.42113 10.433 3.57741 10.5892C3.73369 10.7455 3.94565 10.8333 4.16666 10.8333C4.38768 10.8333 4.59964 10.7455 4.75592 10.5892C4.9122 10.433 4.99999 10.221 4.99999 9.99999C4.99999 9.77898 4.9122 9.56701 4.75592 9.41073C4.59964 9.25445 4.38768 9.16666 4.16666 9.16666C3.94565 9.16666 3.73369 9.25445 3.57741 9.41073C3.42113 9.56701 3.33333 9.77898 3.33333 9.99999ZM9.16666 9.99999C9.16666 10.221 9.25446 10.433 9.41074 10.5892C9.56702 10.7455 9.77898 10.8333 9.99999 10.8333C10.221 10.8333 10.433 10.7455 10.5893 10.5892C10.7455 10.433 10.8333 10.221 10.8333 9.99999C10.8333 9.77898 10.7455 9.56701 10.5893 9.41073C10.433 9.25445 10.221 9.16666 9.99999 9.16666C9.77898 9.16666 9.56702 9.25445 9.41074 9.41073C9.25446 9.56701 9.16666 9.77898 9.16666 9.99999ZM15 9.99999C15 10.221 15.0878 10.433 15.2441 10.5892C15.4004 10.7455 15.6123 10.8333 15.8333 10.8333C16.0543 10.8333 16.2663 10.7455 16.4226 10.5892C16.5789 10.433 16.6667 10.221 16.6667 9.99999C16.6667 9.77898 16.5789 9.56701 16.4226 9.41073C16.2663 9.25445 16.0543 9.16666 15.8333 9.16666C15.6123 9.16666 15.4004 9.25445 15.2441 9.41073C15.0878 9.56701 15 9.77898 15 9.99999Z"
                        stroke="#524435"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>
        </div>
    );
}


export default function ProfileHeader({
    activeProfileTab,
    profileData,
    isFollowing,
    profileStats,
    refreshProfile,
    setActiveProfileTab,
    setActiveTab,
    showHeadlineCreator,
    setContextMenuVisible,
    onBackPress,
}: {
    activeProfileTab: ProfileTab,
    profileData: DBAccount|null,
    isFollowing: boolean,
    profileStats: ProfileDataStats,
    refreshProfile: () => void,
    setActiveProfileTab: (tab: ProfileTab) => void
    setActiveTab: (tab: TabName) => void
    showHeadlineCreator: () => void
    setContextMenuVisible: (visible: boolean) => void;
    onBackPress: () => void;
})
{
    const userData = useContext(AuthContext);

    const [editMode, setEditMode] = useState(false);

    useEffect(() =>
    {
        const triggerOpenProfileEditMode = () =>
        {
            Logger.log("Open profile edit mode triggered");
            setEditMode(!editMode);
        };

        UIListener.listen("openProfileEditMode", triggerOpenProfileEditMode);

        return () =>
        {
            UIListener.unlisten("openProfileEditMode", triggerOpenProfileEditMode);
        };
    }, [editMode]);

    // if (!profileData)
    // {
    //     return null;
    // }

    const isValidPhoto = (photoPath: string | null) =>
    {
        return photoPath !== null && photoPath.trim() !== "" && photoPath !== "{}";
    };


    return (
        <div>
            <ProfileTopControls
                profileData={profileData}
                setActiveTab={setActiveTab}
                setContextMenuVisible={setContextMenuVisible}
                onBackPress={onBackPress}
            />
            <div
                className={Wind.style({
                    position: "relative",
                    aspectRatio: "aspect-[398/152]",
                    marginX: "mx-2",
                    display: "flex",
                    flexDirection: "flex-col",
                    paddingTop: "pt-16"
                }).class}
            >
                <div
                    className={Wind.style({
                        position: "absolute",
                        top: "top-0",
                        left: "left-0",
                        width: "w-full",
                        height: "h-full",
                        zIndex: "z-[2]",
                        pointerEvents: "pointer-events-none",
                    }).class}
                    style={{
                        backgroundImage: `url(${Mountains})`,
                        backgroundSize: "contain",
                        backgroundPosition: "top left",
                        backgroundRepeat: "no-repeat",
                    }}
                />
                <div
                    className={Wind.style({
                        backgroundColor: "bg-[#524435]",
                        paddingTop: "pt-10",
                        position: "relative",
                    }).class}
                >
                    { }
                    {profileData !== null && !isValidPhoto(profileData.photo_path) ? (
                        <DEFAULT_AVATAR
                            className={Wind.style({
                                position: "absolute",
                                top: "-top-[2.25rem]",
                                left: "left-3",
                                width: "w-[4.5rem]",
                                height: "h-[4.5rem]",
                                borderRadius: "rounded-full",
                                borderWidth: "border-4",
                                borderColor: "border-white",
                                backgroundColor: "bg-[#695946]",
                                color: "text-white",
                                zIndex: "z-[3]",
                                display: "flex",
                                alignItems: "items-center",
                                justifyContent: "justify-center",
                                filterDropShadow: "drop-shadow-[0_0_0.5rem_rgba(0,0,0,0.5)]",
                                padding: "p-1",
                            }).class}
                        />
                    ) : (
                        <div
                            className={Wind.style({
                                position: "absolute",
                                top: "-top-[2.25rem]",
                                left: "left-3",
                                width: "w-[4.5rem]",
                                height: "h-[4.5rem]",
                                borderRadius: "rounded-full",
                                borderWidth: "border-4",
                                borderColor: "border-white",
                                zIndex: "z-[3]",
                                display: "flex",
                                alignItems: "items-center",
                                justifyContent: "justify-center",
                                filterDropShadow: "drop-shadow-[0_0_0.5rem_rgba(0,0,0,0.5)]",
                                padding: "p-0",
                            }).class}
                            style={{
                                backgroundImage: `url(${profileData?.photo_path})`,
                                backgroundSize: "cover",
                            }}
                        />
                    )}
                    <div
                        className={Wind.style({
                            paddingX: "px-4",
                        }).class}
                    >
                        <div
                            className={Wind.style({
                                display: "flex",
                                alignItems: "items-center",
                                justifyContent: "justify-between",
                            }).class}
                        >
                            <h1
                                className={Wind.style({
                                    color: "text-white",
                                    display: "flex",
                                    flexDirection: "flex-row",
                                    alignItems: "items-center",
                                    paddingY: "py-1",
                                    gap: "gap-1",
                                    fontWeight: "font-semibold",
                                }).class}
                            >
                                <span>
                                    {profileData === null ? <TextPlaceholder /> : profileData.username}
                                </span>
                                {profileData?.verified && (
                                    <VerifiedBadge
                                        className={Wind.style({
                                            width: "w-4",
                                            height: "h-4",
                                            color: "text-[#F9DC5C]",
                                        }).class}
                                        title="Verified Profile"
                                        aria-label="Verified Profile"
                                    />
                                )}
                                {isTwitchConnected(profileData) && (
                                    <TwitchIcon
                                        className={Wind.style({
                                            width: "w-4",
                                            height: "h-4",
                                            fill: "fill-white",
                                        }).class}
                                    />
                                )}
                            </h1>
                            {userData
                                && profileData !== null
                                && userData.id.toString() !== profileData.id.toString()
                                && (
                                    <FollowButton
                                        profile_id={profileData.id}
                                        isFollowing={isFollowing}
                                        onChange={() =>
                                        {
                                            refreshProfile();
                                        }}
                                    />
                                )
                            }
                        </div>

                        <p
                            className={Wind.style({
                                fontSize: "text-xs",
                                lineHeight: "leading-3",
                                marginBottom: "mb-3",
                                color: "text-[#E6D6C4]"
                            }).class}
                        >
                            {profileData === null ? <TextPlaceholder /> : profileData.bio}
                        </p>
                    </div>
                    <div
                        className={Wind.style({
                            display: "flex",
                            gap: "gap-2",
                            paddingX: "px-4",
                        }).class}
                    >
                        <ProfileStat
                            name="Realization"
                            value={profileStats.realizationsCount}
                        />
                        <ProfileStat
                            name="Headline"
                            value={profileStats.headlinesCreated}
                        />
                        <ProfileStat
                            name="Follower"
                            value={profileStats.followersCount}
                        />
                    </div>
                    {userData
                        && profileData !== null
                        && profileData.id.toString() === userData.id
                        && (
                            <div className="w-full">
                                <button
                                    className={Wind.style({
                                        marginX: "mx-4",
                                        height: "h-10",
                                        backgroundColor: "bg-poppy-yellow",
                                        color: "text-[#0F0A04]",
                                        fontFamily: "font-poppins",
                                        fontSize: "text-sm",
                                        fontWeight: "font-normal",
                                        borderRadius: "rounded-full",
                                        width: "w-[calc(100%-2rem)]",
                                        marginBottom: "mb-3",
                                        display: "flex",
                                        alignItems: "items-center",
                                        justifyContent: "justify-center",
                                        gap: "gap-2",
                                    }).class}
                                    onClick={() =>
                                    {
                                        showHeadlineCreator();
                                    }}
                                >
                                    <EditorHatIcon />
                                    Create Headline
                                </button>
                            </div>
                        )
                    }
                </div>
                <div
                    className={Wind.style({
                        backgroundColor: "bg-[#3B2F21]",
                        height: "h-10",
                        borderBottomRadius: "rounded-b-[2.5rem]",
                        display: "grid",
                        gridTemplateColumns: "grid-cols-2",
                    }).class}
                >
                    <div
                        className={Wind.style({
                            display: "flex",
                            alignItems: "items-center",
                            justifyContent: "justify-center",
                        }).class}
                    >
                        <TabButton
                            name="Headlines"
                            icon={
                                <EditorHatIcon
                                    className={Wind.style({
                                        width: "w-6",
                                        height: "h-6",
                                    }).class}
                                />
                            }
                            isActive={activeProfileTab === ProfileTab.HEADLINES}
                            onClick={() =>
                            {
                                setActiveProfileTab(ProfileTab.HEADLINES);
                            }}
                        />
                    </div>
                    <div
                        className={Wind.style({
                            display: "flex",
                            alignItems: "items-center",
                            justifyContent: "justify-center",
                        }).class}
                    >
                        <TabButton
                            name="Realizations"
                            icon={
                                <CameraIcon
                                    className={Wind.style({
                                        width: "w-6",
                                        height: "h-6",
                                    }).class}
                                />
                            }
                            isActive={activeProfileTab === ProfileTab.REALIZATIONS}
                            onClick={() =>
                            {
                                setActiveProfileTab(ProfileTab.REALIZATIONS);
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
